import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { CheckOutlined, EyeOutlined, UndoOutlined } from '@ant-design/icons';
import DataGrid from '../../../../common/DataGrid/DataGrid.jsx';
import IconButton from '../../../../common/controls/IconButton/IconButton.jsx';
import { Tooltip } from '../../../../common/index.jsx';
import { useDataGridTextFilter } from '../../../../common/DataGrid/filters/useDataGridTextFilter.jsx';
import { useDataGridDateRangeFilter } from '../../../../common/DataGrid/filters/useDataGridDateRangeFilter.jsx';
import {
  dataGridDateSorter,
  dataGridTextSorter,
} from '../../../../common/DataGrid/sorters/DataGridSorters.jsx';
import { DATETIME_FORMAT_DATETIME } from '../../../../../utils/helpers/CommonHelper.jsx';
import {
  BugReportStateTag,
  bugReportReporterName,
  isBugReportResolved,
} from '../bugReportHelpers.jsx';

const BugReportsTable = ({
  bugReports,
  onPreview,
  onSetResolved,
  canResolve = false,
}) => {
  const { t } = useTranslation();

  // flat reporter name, so it can be filtered and sorted like other columns
  const rows = useMemo(
    () =>
      (Array.isArray(bugReports) ? bugReports : []).map((bugReport) => ({
        ...bugReport,
        reporter_display_name: bugReportReporterName(bugReport),
        reporter_sort_name: [
          bugReport.person?.last_name,
          bugReport.person?.first_name,
          bugReport.reporter_name,
        ]
          .filter(Boolean)
          .join(' '),
      })),
    [bugReports],
  );

  const textFilterProps = useDataGridTextFilter('text');
  const reporterFilterProps = useDataGridTextFilter('reporter_display_name');
  const createdAtFilterProps = useDataGridDateRangeFilter('created_at');

  // the text takes the remaining space, the other columns have fixed widths that fit their content
  const columns = [
    {
      title: t('Text chyby'),
      dataIndex: 'text',
      ellipsis: { showTitle: false },
      sorter: dataGridTextSorter('text'),
      render: (text) => (
        <Tooltip tooltip={text}>
          <span>{text || '–'}</span>
        </Tooltip>
      ),
      ...textFilterProps,
    },
    {
      title: t('Meno a priezvisko'),
      dataIndex: 'reporter_display_name',
      width: 200,
      ellipsis: true,
      sorter: dataGridTextSorter('reporter_sort_name'),
      ...reporterFilterProps,
    },
    {
      title: t('Dátum nahlásenia'),
      dataIndex: 'created_at',
      width: 190,
      responsive: ['md'],
      sorter: dataGridDateSorter('created_at'),
      defaultSortOrder: 'descend',
      render: (date) => (date ? dayjs(date).format(DATETIME_FORMAT_DATETIME) : '–'),
      ...createdAtFilterProps,
    },
    {
      title: t('Stav'),
      dataIndex: 'resolved_at',
      width: 130,
      responsive: ['lg'],
      render: (_, row) => <BugReportStateTag bugReport={row} t={t} />,
      filters: [
        { text: t('Nevyriešená'), value: 'unresolved' },
        { text: t('Vyriešená'), value: 'resolved' },
      ],
      onFilter: (value, row) =>
        (value === 'resolved') === isBugReportResolved(row),
    },
  ];

  return (
    <DataGrid
      uniqueTableId="bug-reports"
      rowKey={(row) => row.id}
      columns={columns}
      dataSource={rows}
      onRowClick={(row) => onPreview(row.id)}
      optionsWidth={150}
      customSettings={(row) => (
        <div style={{ display: 'flex' }}>
          <IconButton
            icon={<EyeOutlined />}
            tooltip={t('Náhľad nahlásenia')}
            onClick={(e) => {
              e.stopPropagation();
              onPreview(row.id);
            }}
          />
          {canResolve &&
            (isBugReportResolved(row) ? (
              <IconButton
                icon={<UndoOutlined />}
                tooltip={t('Označiť ako nevyriešenú')}
                onClick={(e) => {
                  e.stopPropagation();
                  onSetResolved(row.id, false);
                }}
              />
            ) : (
              <IconButton
                icon={<CheckOutlined />}
                tooltip={t('Označiť ako vyriešenú')}
                onClick={(e) => {
                  e.stopPropagation();
                  onSetResolved(row.id, true);
                }}
              />
            ))}
        </div>
      )}
    />
  );
};

BugReportsTable.propTypes = {
  bugReports: PropTypes.array,
  onPreview: PropTypes.func.isRequired,
  onSetResolved: PropTypes.func.isRequired,
  canResolve: PropTypes.bool,
};

export default BugReportsTable;
